import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import "./Dashboard.css";
import { ProjectData } from "./types";
import { clearMessages } from "../utils/errorHandling";
import { transformProjectsListBackendToFrontend } from "../utils/dataTransformation";
import { apiClient } from "@/utils/apiClient/apiClient";

const Dashboard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recentProjects, setRecentProjects] = useState<ProjectData[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState<Boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  const handleNewProject = () => {
    localStorage.removeItem("unsavedProject");

    // For a new project, we don't create it on the backend yet.
    // We navigate to the location selector with a special 'new' identifier.
    // The project data will be managed in the subsequent components and saved
    // locally until the user decides to save it to the server.
    navigate(`/projects/new/location`);
  };

  const handleOpenProject = (projectId: string) => {
    navigate(`/projects/${projectId}/map`);
  };

  const loadRecentProjects = async () => {
    setIsLoadingProjects(true);
    clearMessages(setError, setIsError);
    try {
      const data = await apiClient.getProjects(null);

      if (Array.isArray(data)) {
        const projects: ProjectData[] =
          transformProjectsListBackendToFrontend(data);
        setRecentProjects(projects);
      } else {
        throw new Error(
          `Unexpected response format from server when fetching recent projects: $(data)`
        );
      }
    } catch (error: any) {
      console.error("Error fetching recent projects:", error);
      setError(
        error?.message ||
          "An error occurred while fetching recent projects. Please try again."
      );
      setIsError(true);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  useEffect(() => {
    loadRecentProjects();
  }, []);

  return (
    <div className="dashboard">
      <Header />
      <div className="dashboard-content">
        <div className="projects-section">
          <div className="projects-header">
            <h2>{t("dashboard.projects")}</h2>
            <button className="new-project-button" onClick={handleNewProject}>
              {t("dashboard.newProject")} +
            </button>
          </div>

          <div className="recent-projects">
            <h3>{t("dashboard.recentProjects")}</h3>
            {isLoadingProjects ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <h2>{t('dashboard.loadingProjects')}</h2>
                <p>{t('dashboard.wait')}</p>
              </div>
            ) : isError ? (
              <div className="text-center py-5">
                <p className="error-message-tab">{error}</p>
                <button
                  className="btn btn-outline-primary mt-3"
                  onClick={loadRecentProjects}
                  disabled={isLoadingProjects}
                >
                  {isLoadingProjects ? t('dashboard.retry') : t('dashboard.tryAgain')}
                </button>
              </div>
            ) : recentProjects.length > 0 ? (
              <div className="projects-grid">
                {recentProjects.map((project) => (
                  <div key={project.id} className="project-card">
                    <div className="project-card-header">
                      <h4>{project.name}</h4>
                      <span className={`status-badge ${project.status}`}>
                        {t(`dashboard.status.${project.status}`, project.status)}
                      </span>
                    </div>
                    <p className="last-modified">
                      {t('dashboard.lastModified')}{" "}
                      {new Date(project.lastModified).toLocaleDateString()}
                    </p>
                    <div className="project-card-actions">
                      <button
                        className="open-project-button"
                        onClick={() => handleOpenProject(project.id)}
                      >
                        {t('dashboard.openProject')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-projects">
                <p>{t("dashboard.noProjects")}</p>
                <button
                  className="create-first-project-button"
                  onClick={handleNewProject}
                >
                  {t("dashboard.createFirstProject")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-sidebar">
          <div className="quick-stats">
            <h3>{t("dashboard.quickStats")}</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-number">{recentProjects.length}</span>
                <span className="stat-label">{t('dashboard.activeProjects')}</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">
                  {
                    recentProjects.filter((p) => p.status === "completed")
                      .length
                  }
                </span>
                <span className="stat-label">{t('dashboard.completed')}</span>
              </div>
            </div>
          </div>

          <div className="recent-activity">
            <h3>{t("dashboard.recentActivity")}</h3>
            <div className="activity-list">
              {/* Add recent activity items here */}
              <p className="no-activity">{t("dashboard.noRecentActivity")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
